import { Stack } from '@mui/material';

import { api } from 'common';

import { LegacyOcrFields } from './LegacyOcrFields.tsx';
import { VisionLanguageModelFields } from './VisionLanguageModelFields.tsx';
import { useReactFlowViewContext } from '../../../../context/context';
import { BlockValueTemplateField } from '../../../ConfigFields/CodeMirror/FlowTemplate.tsx';
import { BlockSelectField } from '../../../ConfigFields/SelectField.tsx';
import { useSelectedNodeField } from '../../../ConfigFields/useSelectedNodeField.ts';

const { OcrMethod, OcrDetailLevel, OcrBoundingBoxMode } = api.FlowDefinition;

const methodOptions = [
  { label: 'Vision Language Model', value: OcrMethod.VisionLanguageModel },
  { label: 'Traditional OCR', value: OcrMethod.Legacy },
];

const detailLevelOptions = [
  { label: 'None', value: OcrDetailLevel.None },
  { label: 'Page', value: OcrDetailLevel.Page },
  { label: 'Block', value: OcrDetailLevel.Block },
  { label: 'Paragraph', value: OcrDetailLevel.Paragraph },
  { label: 'Word', value: OcrDetailLevel.Word },
  { label: 'Symbol', value: OcrDetailLevel.Symbol },
];

const boundingBoxModeOptions = [
  { label: 'Disabled', value: OcrBoundingBoxMode.None },
  { label: 'Enabled', value: OcrBoundingBoxMode.All },
];

export function OcrV2BlockFormFields() {
  const { value: method } =
    useSelectedNodeField<api.FlowDefinition.OcrV2Block['config']['method']>('config.method');

  const { value: prompt } =
    useSelectedNodeField<api.FlowDefinition.OcrV2Block['config']['prompt']>('config.prompt');

  const { value: detailLevel } =
    useSelectedNodeField<api.FlowDefinition.OcrV2Block['config']['detailLevel']>(
      'config.detailLevel'
    );

  const { value: boundingBoxMode, setValue: setBoundingBoxMode } =
    useSelectedNodeField<api.FlowDefinition.OcrV2Block['config']['boundingBoxMode']>(
      'config.boundingBoxMode'
    );

  const { isEditable } = useReactFlowViewContext();

  const detailLevelSupportsBoundingBoxes = ![OcrDetailLevel.None, OcrDetailLevel.Page].some(
    (level) => level === detailLevel
  );

  // FIXME: Existing code mutates state during render which can cause bugs.
  // Refactor to have a fallback value, and explicitly set it only when the user makes a
  // change via onClick or onChange (Or do a db migration.)
  if (!detailLevelSupportsBoundingBoxes && boundingBoxMode === OcrBoundingBoxMode.All) {
    setBoundingBoxMode(OcrBoundingBoxMode.None);
  }

  return (
    <Stack spacing={2}>
      <BlockValueTemplateField
        fieldName="config.fileReference"
        label="File reference"
        helperText="A string in the format {type}:{identifier}."
      />

      <BlockSelectField
        options={methodOptions}
        label="Processing method"
        fieldName="config.method"
        defaultValue={OcrMethod.Legacy}
        clearable={false}
        helperText={
          method === OcrMethod.VisionLanguageModel
            ? 'Uses Anthropic Claude Opus 4.6 for highest accuracy on checkboxes, handwriting, and complex layouts.'
            : 'Uses Google Vision API for deterministic text extraction with optional bounding box metadata.'
        }
      />

      {method === OcrMethod.VisionLanguageModel ? (
        <VisionLanguageModelFields isEditable={isEditable} prompt={prompt} />
      ) : (
        <LegacyOcrFields
          detailLevelSupportsBoundingBoxes={detailLevelSupportsBoundingBoxes}
          detailLevelOptions={detailLevelOptions}
          boundingBoxModeOptions={boundingBoxModeOptions}
        />
      )}
    </Stack>
  );
}
